import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة

import { 
  Save, ArrowLeft, Warehouse, CreditCard, 
  ShieldCheck, Search, List, Package, Tag, Info, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, Type, Minus, Plus, Paperclip
} from 'lucide-react';

import UniversalLookup from './UniversalLookup';
import GLAccountTree from './GLAccountTree';
import AttachmentsModal from './AttachmentsModal'; // 🎯 المرفقات
import AuditHistoryModal from './AuditHistoryModal';
import SmartPrintButton from './PrintButton';
import { usePermissions } from './usePermissions'; // 🎯 الصلاحيات
import { useFontSize } from './FontSizeContext'; // 🎯 التحكم في الخط

const StoreroomDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  
  const { canCreate, canUpdate, canDelete } = usePermissions('storerooms');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  const [activeTab, setActiveTab] = useState('MAIN'); 
  const [inventory, setInventory] = useState([]); 
  const [loadingInv, setLoadingInv] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [count, setCount] = useState(0);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

  const [data, setData] = useState({
    location: '', description: '', siteid: sessionSite, orgid: sessionOrg,
    isdefault: false, useinpo: true, invowner: '',
    glaccount: '', controlacc: '', shrinkacc: '', cogs_glaccount: ''
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {} });
  const [glLookup, setGlLookup] = useState({ isOpen: false, title: '', field: '' });

  // --- مكون التحكم في الخط ---
  const FontControls = () => (
    <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
      <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-status-warning transition-colors"><Minus size={14} /></button>
      <div className="flex items-center gap-1 min-w-[40px] justify-center text-white/70">
        <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
      </div>
      <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-status-warning transition-colors"><Plus size={14} /></button>
    </div>
  );

  // 1. جلب البيانات الأساسية
  useEffect(() => {
    if (!isNew) {
      axios.get(`/api/storerooms/${id}`)
        .then(res => setData(res.data))
        .catch(() => toast.error(t('common.load_error', "Error fetching storeroom details")));
    }
  }, [id, isNew, t]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  // 2. جلب المرفقات
  const fetchCount = useCallback(async () => {
    if (!id || isNew) return;
    try {
      const res = await axios.get(`/api/attachments/count/STOREROOM/${id}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Attachment err:", err); }
  }, [id, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  // 3. جلب النافيجيشن بالكويري
  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const token = localStorage.getItem('token'); 
      const res = await axios.get(`/api/storerooms/${id}/navigation${location.search}`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { console.error("Nav failed", err); }
  }, [id, isNew, location.search]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const goTo = (targetId) => targetId && navigate(`/storerooms/${targetId}${location.search}`);

  // 4. جلب الأصناف عند فتح تابة Inventory
  useEffect(() => {
    if (activeTab === 'INVENTORY' && !isNew) {
        setLoadingInv(true);
        axios.get(`/api/storerooms/${id}/inventory`)
          .then(res => setInventory(res.data))
          .catch(() => setInventory([]))
          .finally(() => setLoadingInv(false));
    }
  }, [activeTab, id, isNew]);

  const handleInputChange = (field, value) => {
    setData(prev => {
        if (field === 'orgid') return { ...prev, [field]: value, siteid: '' };
        return { ...prev, [field]: value };
    });
  };

  const handleSmartSearch = (fieldName, title, defaultType) => {
    let extraFilters = {};
    if (fieldName === 'siteid' && data.orgid) {
        extraFilters = { org_id: data.orgid };
    }
    setLookup({ isOpen: true, title, type: defaultType, field: fieldName, filters: extraFilters });
  };

  const handleSave = async () => {
    if (!data.location || data.location.trim() === "") return toast.error(t('storerooms.messages.req_fields', "Location ID is required!"));
    if (!data.orgid || !data.siteid) return toast.error(t('storerooms.messages.req_org_site', "Org and Site are required!"));

    const loadId = toast.loading(t('common.saving', "Saving Storeroom..."));
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/storerooms/save', data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(t('common.save_success', "Saved Successfully"), { id: loadId });
      if (isNew) navigate(`/storerooms/${data.location}${location.search}`);
    } catch (err) {
      toast.error(t('common.save_fail', "Save failed"), { id: loadId });
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('storerooms.messages.delete_warn', { store: data.location }, `You are about to delete Storeroom ${data.location}. This action cannot be undone!`),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
    });

    if (result.isConfirmed) {
      const loadId = toast.loading(t('common.deleting', "Deleting storeroom..."));
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/storerooms/${data.location}`, { headers: { 'Authorization': `Bearer ${token}` } });
        toast.success(t('common.delete_success', "Storeroom deleted successfully"), { id: loadId });
        navigate(`/storerooms${location.search}`);
      } catch (err) {
        toast.error(t('common.delete_fail', "Delete failed"), { id: loadId });
      }
    }
  };

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  return (
    <div className={`flex flex-col h-screen bg-surface-app uppercase tracking-tighter duration-300 transition-[font-size] ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={(row, meta) => {
            const val = (meta && row[meta.valueKey]) || row.site_id || row.org_id || row.comp_num || row.value || row.id;
            handleInputChange(lookup.field, val);
            setLookup(prev => ({ ...prev, isOpen: false }));
        }}
      />

      <GLAccountTree
        isOpen={glLookup.isOpen}
        title={glLookup.title}
        orgId={data.orgid}
        onClose={() => setGlLookup(prev => ({ ...prev, isOpen: false }))}
        onSelect={(node) => {
          handleInputChange(glLookup.field, node.glaccount);
          setGlLookup(prev => ({ ...prev, isOpen: false }));
        }}
      />

      {isDocModalOpen && !isNew && (
        <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); fetchCount();}} ownertable="STOREROOM" ownerid={id} />
      )}
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="storerooms" recordId={data.location} />

      {/* --- TOP TOOLBAR --- */}
      <div className="bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/storerooms${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white/70">
            {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18}/>}
          </button>

          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
                <span className="text-status-warning font-black">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}

          <div className="flex flex-col ml-4">
              <span className="text-[0.8em] text-status-warning font-bold uppercase tracking-widest">{t('storerooms.details.profile', 'Storeroom')}</span>
              <h1 className="text-sm font-black tracking-tighter">{data.location || t('common.new', 'NEW RECORD')}</h1>
          </div>
        </div>

        <div className={`flex items-center gap-2 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
            <button onClick={handleSave} className="bg-status-success hover:bg-status-success text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all uppercase">
              <Save size={14}/> {t('common.save', 'SAVE CHANGES')}
            </button>
          )}

          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 hover:bg-status-danger/20 text-status-danger rounded transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={16}/>
            </button>
          )}

          <SmartPrintButton moduleName="STOREROOM" refId={data.location} />

          {!isNew && (
            <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-white/70" title={t('common.attachments', 'Attachments')}>
              <Paperclip size={16} />
              {count > 0 && <span className="absolute top-1 right-1 bg-status-danger text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
            </button>
          )}

          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          <FontControls />
        </div>
      </div>

      {/* --- HEADER INFO --- */}
      <div className="bg-surface-card border-b border-line px-8 py-4 flex items-center gap-12 shadow-sm z-20">
        <div className="flex flex-col min-w-[200px]">
          <span className="text-[0.8em] text-ink-secondary font-black uppercase tracking-widest">{t('storerooms.fields.location', 'Storeroom')}</span>
          <input
              className={`bg-transparent outline-none font-black text-primary-dark text-xl w-32 border-b-2 border-transparent focus:border-status-warning transition-all ${isRTL ? 'text-right' : 'text-left'}`}
              value={data.location}
              disabled={!isNew}
              onChange={(e) => handleInputChange('location', e.target.value.toUpperCase())}
              placeholder={t('common.new', 'NEW')}
          />
        </div>

        <div className={`flex flex-col flex-1 border-line px-8 ${isRTL ? 'border-r' : 'border-l'}`}>
          <label className="text-[0.8em] text-ink-secondary font-black uppercase tracking-widest">{t('assets.headers.desc', 'Description')}</label>
          <span className="text-sm font-bold text-ink mt-1 truncate">{data.description || t('storerooms.details.record', 'Storeroom Record')}</span>
        </div>


      </div>

      {/* --- TABS --- */}
      <div className="flex bg-primary-dark text-white shadow-md px-4 overflow-x-auto no-scrollbar">
        {[
          { id: 'MAIN', label: t('storerooms.tabs.main', 'MAIN'), icon: <List size={14}/> },
          { id: 'INVENTORY', label: t('storerooms.tabs.inventory', 'INVENTORY'), icon: <Package size={14}/> }
        ].map(tb => (
          <button key={tb.id} onClick={() => setActiveTab(tb.id)}
            className={`px-8 py-3 flex items-center gap-2 font-black border-white/5 transition-all whitespace-nowrap ${isRTL ? 'border-l' : 'border-r'} ${activeTab === tb.id ? 'bg-surface-card text-primary-dark' : 'hover:bg-white/10 opacity-75'}`}>
            {tb.icon} {tb.label}
          </button>
        ))}
      </div>

      {/* --- Main Content --- */}
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-surface-panel">
        {activeTab === 'MAIN' ? (
            <div className="bg-surface-card p-10 border border-line shadow-sm max-w-5xl mx-auto space-y-10 animate-in fade-in duration-500 rounded-sm border-t-4 border-t-primary">
                <div className="grid grid-cols-2 gap-20">
                    <div className="space-y-6">
                        <h3 className={`text-[0.9em] font-black text-ink-secondary border-b border-line pb-2 tracking-[0.2em] flex items-center gap-2 `}>
                            <Info size={14} className="text-primary"/> {t('storerooms.details.gen_settings', 'General Settings')}
                        </h3>
                        <EditField label={t('assets.headers.desc', 'Description')} value={data.description} onChange={v => handleInputChange('description', v)} isRTL={isRTL} />
                        <EditField label={t('common.org', 'Organization')} disabled={true} value={data.orgid}  isRTL={isRTL} />
                        <EditField label={t('common.site', 'Site')} disabled={true} value={data.siteid}  isRTL={isRTL} />
                        <EditLookupField label={t('storerooms.fields.invowner', 'Inventory Owner')} value={data.invowner} onSearch={() => setLookup({isOpen:true, title: t('storerooms.lookup.person', 'Select Person'), type:'persons', field:'invowner'})} isRTL={isRTL} />

                        <div className={`flex gap-10 pt-4 `}>
                            <label className={`flex items-center gap-3 cursor-pointer group `}>
                                <input type="checkbox" checked={data.isdefault} onChange={e => handleInputChange('isdefault', e.target.checked)} className="w-4 h-4 accent-primary" />
                                <span className="font-bold text-ink-secondary group-hover:text-primary transition-colors uppercase">{t('storerooms.fields.isdefault', 'Default Storeroom?')}</span>
                            </label>
                            <label className={`flex items-center gap-3 cursor-pointer group `}>
                                <input type="checkbox" checked={data.useinpo} onChange={e => handleInputChange('useinpo', e.target.checked)} className="w-4 h-4 accent-primary" />
                                <span className="font-bold text-ink-secondary group-hover:text-primary transition-colors uppercase">{t('storerooms.fields.useinpo', 'Use in PO?')}</span>
                            </label>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <h3 className={`text-[0.9em] font-black text-ink-secondary border-b border-line pb-2 tracking-[0.2em] flex items-center gap-2 `}>
                            <CreditCard size={14} className="text-status-success"/> {t('storerooms.details.gl_accounts', 'GL Accounts')}
                        </h3>
                        <EditLookupField label={t('storerooms.fields.glaccount', 'Inventory Asset Account')} value={data.glaccount} onSearch={() => setGlLookup({ isOpen: true, title: t('storerooms.fields.glaccount', 'Inventory Asset Account'), field: 'glaccount' })} isRTL={isRTL} />
                        <EditLookupField label={t('storerooms.fields.cogs_glaccount', 'COGS Account')} value={data.cogs_glaccount} onSearch={() => setGlLookup({ isOpen: true, title: t('storerooms.fields.cogs_glaccount', 'COGS Account'), field: 'cogs_glaccount' })} isRTL={isRTL} />
                        <EditLookupField label={t('storerooms.fields.controlacc', 'Control Account (GRNI)')} value={data.controlacc} onSearch={() => setGlLookup({ isOpen: true, title: t('storerooms.fields.controlacc', 'Control Account (GRNI)'), field: 'controlacc' })} isRTL={isRTL} />
                        <EditLookupField label={t('storerooms.fields.shrinkacc', 'Shrinkage Account')} value={data.shrinkacc} onSearch={() => setGlLookup({ isOpen: true, title: t('storerooms.fields.shrinkacc', 'Shrinkage Account'), field: 'shrinkacc' })} isRTL={isRTL} />

                        <div className={`bg-primary-soft/50 p-6 rounded mt-12 shadow-inner ${isRTL ? 'border-r-4 border-r-primary-dark text-right' : 'border-l-4 border-l-primary-dark text-left'}`}>
                            <div className={`flex items-center gap-2 text-primary-dark font-black mb-1 uppercase tracking-widest text-[0.8em] `}>
                                <ShieldCheck size={16}/> <span>{t('storerooms.details.inv_control', 'Inventory Control')}</span>
                            </div>
                            <p className="text-ink-secondary italic text-[0.9em] leading-relaxed lowercase">
                                {t('storerooms.details.inv_control_desc', 'all material issues and receipts for this storeroom will be balanced against these accounts.')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            /* --- Inventory Tab --- */
            <div className="bg-surface-card border border-line shadow-2xl max-w-6xl mx-auto rounded-sm overflow-hidden animate-in slide-in-from-right duration-300 border-t-4 border-t-primary-dark">
                <div className={`bg-surface-panel p-4 px-8 border-b border-line flex justify-between items-center `}>
                    <div className={`flex items-center gap-3 `}>
                        <div className="p-2 bg-primary rounded text-white shadow-md"><Package size={16}/></div>
                        <div>
                            <span className="font-black tracking-widest text-primary-dark uppercase">{t('storerooms.tabs.inventory', 'Current Inventory Balances')}</span>
                            <p className="text-[0.8em] text-ink-secondary font-bold mt-0.5 uppercase tracking-tighter">{t('storerooms.fields.location', 'Location')}: {data.location}</p>
                        </div>
                    </div>
                    <div className={`flex items-center gap-4 `}>
                        <div className={isRTL ? 'text-left' : 'text-right'}>
                            <span className="text-[0.8em] font-black text-ink-secondary block uppercase italic">{t('storerooms.details.items_stocked', 'Items Stocked')}</span>
                            <span className="text-lg font-black text-primary-dark leading-none">{inventory.length}</span>
                        </div>
                        <button onClick={() => toast.success(t('common.refresh', "Refreshed"))} className="p-2 text-ink-secondary hover:text-primary transition-all"><RefreshCw size={16}/></button>
                    </div>
                </div>

                <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-surface-panel border-b border-line text-ink-secondary font-black uppercase text-[0.8em] tracking-tighter">
                        <tr>
                            <th className={`p-4 border-line-subtle w-32 ${isRTL ? 'border-l' : 'border-r'}`}>{t('storerooms.tables.itemnum', 'Item Num')}</th>
                            <th className={`p-4 border-line-subtle ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc', 'Description')}</th>
                            <th className={`p-4 border-line-subtle w-20 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('storerooms.tables.uom', 'UOM')}</th>
                            <th className={`p-4 border-line-subtle w-32 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{t('storerooms.tables.curbal', 'Current Bal')}</th>
                            <th className={`p-4 border-line-subtle w-32 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{t('storerooms.tables.reserved', 'Reserved Qty')}</th>
                            <th className="p-4 w-28 text-center uppercase">{t('assets.headers.status', 'Status')}</th>
                        </tr>
                    </thead>
                    <tbody className="text-[0.9em] font-bold">
                        {loadingInv ? (
                            <tr><td colSpan="6" className="p-20 text-center animate-pulse text-ink-secondary uppercase font-black tracking-[0.2em]">{t('common.syncing', 'Syncing...')}</td></tr>
                        ) : inventory.length === 0 ? (
                            <tr><td colSpan="6" className="p-20 text-center text-ink-secondary italic font-black uppercase tracking-widest">{t('common.no_records', 'No records found')}</td></tr>
                        ) : inventory.map((item, idx) => (
                            <tr key={idx} className="border-b border-line hover:bg-primary-soft/50 transition-colors group uppercase">
                                <td className={`p-4 border-line-subtle font-black text-primary-dark uppercase group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>
                                    <Tag size={10} className={`inline text-primary/50 ${isRTL ? 'ml-2' : 'mr-2'}`}/> {item.itemnum}
                                </td>
                                <td className={`p-4 border-line-subtle text-ink ${isRTL ? 'border-l' : 'border-r'}`}>{item.description}</td>
                                <td className={`p-4 border-line-subtle text-center text-ink-secondary font-black ${isRTL ? 'border-l' : 'border-r'}`}>{item.orderunit || 'EA'}</td>
                                <td className={`p-4 border-line-subtle text-status-success font-mono text-[1.1em] ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>
                                    {parseFloat(item.curbal || 0).toFixed(2)}
                                </td>
                                <td className={`p-4 border-line-subtle text-status-warning font-mono ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>
                                    {parseFloat(item.reserved || 0).toFixed(2)}
                                </td>
                                <td className="p-4 text-center">
                                    <span className={`px-3 py-1 rounded-sm text-[0.8em] font-black uppercase shadow-sm ${item.status === 'ACTIVE' ? 'bg-status-success-bg text-status-success' : 'bg-surface-panel text-ink-secondary'}`}>
                                      {item.status || 'ACTIVE'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
      </div>
    </div>
  );
};

// --- Reusable Components ---

const EditField = ({ label, value, onChange, isRTL }) => (
  <div className={`flex flex-col border-b border-line focus-within:border-primary transition-all group ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.8em] font-black text-ink-secondary uppercase tracking-tighter group-focus-within:text-primary">{label}</label>
    <input
        className={`font-bold text-primary-dark outline-none py-1.5 bg-transparent text-[0.9em] uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`}
        value={value || ''}
        onChange={(e)=>onChange(e.target.value)}
    />
  </div>
);

const EditLookupField = ({ label, value, onSearch, desc, isRTL }) => (
  <div className={`flex flex-col border-b border-line group focus-within:border-primary transition-all ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.8em] font-black text-ink-secondary uppercase tracking-tighter group-focus-within:text-primary">{label}</label>
    <div className={`flex items-center justify-between py-1 `}>
      <div className={`flex items-center gap-2 `}>
        <input
          className={`font-black text-primary-dark outline-none bg-transparent flex-1 uppercase text-[0.9em] ${!value ? 'italic font-normal text-ink-secondary' : ''} ${isRTL ? 'text-right' : 'text-left'}`}
          value={value || 'Select...'}
          readOnly
        />
        {desc && <span className="text-[0.8em] text-ink-secondary italic whitespace-nowrap lowercase">({desc})</span>}
      </div>
      <div className="p-1 hover:bg-primary-soft rounded-full cursor-pointer transition-colors" onClick={onSearch}>
        <Search size={14} className="text-primary" />
      </div>
    </div>
  </div>
);

export default StoreroomDetails;